import { InboxArea, InboxTab } from '@tools/ui/enums';
import { IFilterMap } from '@tools/ui/types';
import {
  EnumTaskStatus,
  FilterFindManyTaskInput
} from '@tools/ui/types/generated/graphql';

export const createBaseFilter = (
  userId?: string,
  taskIds?: string[] | null,
  isConcernAreasMode?: boolean
): Record<
  Extract<InboxArea, InboxArea.personal | InboxArea.general>,
  Record<
    Extract<
      InboxTab,
      InboxTab.live | InboxTab.pending | InboxTab.archive | InboxTab.escalated
    >,
    FilterFindManyTaskInput
  >
> => ({
  [InboxArea.personal]: {
    [InboxTab.live]: {
      _operators: {
        status: {
          nin: [
            EnumTaskStatus.completed,
            EnumTaskStatus.isNotLive,
            EnumTaskStatus.pending,
            EnumTaskStatus.escalated
          ]
        },
        parentTaskId: null,
        assignedTo: { in: [userId] }
      }
    },
    [InboxTab.pending]: {
      _operators: {
        lastDecisionDetails: { lastDecisionMakerId: { in: [userId] } },
        status: { in: [EnumTaskStatus.pending] },
        assignedTo: { in: [userId] },
        parentTaskId: null
      }
    },
    [InboxTab.archive]: {
      _operators: {
        lastDecisionDetails: { lastDecisionMakerId: { in: [userId] } },
        ...(isConcernAreasMode
          ? {}
          : { status: { in: [EnumTaskStatus.completed] } }),
        parentTaskId: null
      }
    },
    [InboxTab.escalated]: {
      _operators: {
        assignedTo: { in: [userId] },
        status: { in: [EnumTaskStatus.escalated] },
        parentTaskId: null
      }
    }
  },
  [InboxArea.general]: {
    [InboxTab.live]: {
      _operators: {
        status: {
          nin: [
            EnumTaskStatus.completed,
            EnumTaskStatus.isNotLive,
            EnumTaskStatus.pending,
            EnumTaskStatus.escalated
          ]
        },
        parentTaskId: null
      }
    },
    [InboxTab.pending]: {
      _operators: {
        status: { in: [EnumTaskStatus.pending] },
        parentTaskId: null
      }
    },
    [InboxTab.archive]: {
      _operators: {
        status: { in: [EnumTaskStatus.completed] },
        parentTaskId: null
      }
    },
    [InboxTab.escalated]: {
      _operators: {
        _id: { in: taskIds },
        status: { nin: [EnumTaskStatus.completed] },
        parentTaskId: null
      }
    }
  }
});

export enum InboxFilterMapperEnum {
  isPaymentExpedited = 'entityCachedData.features.isPaymentExpedited',
  isSuspectedOfProhibitedIndustries = 'entityCachedData.features.isSuspectedOfProhibitedIndustries',
  reason = 'entityCachedData.riskEngineDecision.reason',
  decisionReason = 'entityCachedData.paymentApprovalAction.reason',
  paymentUserEmail = 'entityCachedData.payment.createdByEmail',
  clientIpAgeForOrganization = 'entityCachedData.features.clientIpAgeForOrganization',
  clientIpAgeForUser = 'entityCachedData.features.clientIpAgeForUser',
  isFRR = 'entityCachedData.features.isFRR',
  isPaymentOriginSafe = 'entityCachedData.features.isPaymentOriginSafe',
  fundingSourceType = 'entityCachedData.features.fundingSourceType',
  fundingSourceId = 'entityCachedData.fundingSourceId',
  paymentCreatedAt = 'entityCachedData.payment.createdAt',
  deliveryMethodType = 'entityCachedData.deliveryMethod.deliveryType',
  deliveryMethodCreatedAt = 'entityCachedData.deliveryMethod.createdAt',
  scheduledDate = 'entityCachedData.payment.scheduledDate',
  amount = 'entityCachedData.payment.amount',
  paymentDirection = 'entityCachedData.payment.direction',
  paymentCreateOrigin = 'entityCachedData.payment.createOrigin',
  paymentPartner = 'entityCachedData.payment.createdByPartner.companyName',
  organizationPartner = 'entityCachedData.organization.partner.partnerName',
  vendorCompanyName = 'entityCachedData.vendor.companyName',
  vendorId = 'entityCachedData.vendor.id',
  organizationCompanyName = 'entityCachedData.organization.companyName',
  organizationTaxId = 'entityCachedData.organization.taxId',
  organizationTaxIdType = 'entityCachedData.organization.taxIdType',
  organizationId = 'entityCachedData.organization.id',
  organizationDecision = 'entityCachedData.organization.decision',
  isFinanced = 'entityCachedData.features.isApFinancingPayment',
  organizationKybStatus = 'entityCachedData.organization.kybStatus',
  organizationKybMoneyIn = 'entityCachedData.organization.kybMoneyIn',
  organizationKybMoneyInSubType = 'entityCachedData.organization.kybMoneyInSubType',
  isMelioMeVendor = 'entityCachedData.features.vendorHasHandle',
  paymentId = 'entityCachedData.payment.id',
  paymentRiskStatus = 'entityCachedData.payment.riskStatus',
  lastDecisionDate = 'lastDecisionDetails.lastDecisionDate',
  lastDecisionMakerId = 'lastDecisionDetails.lastDecisionMakerId',
  lastEscalatedReason = 'lastEscalatedDetails.teamIntegrationId',
  lastEscalatedUserId = 'lastEscalatedDetails.userId',
  lastEscalatedDescription = 'lastEscalatedDetails.description',
  label = 'label',
  pendingReason = 'entityCachedData.payment.pendingReason',
  assignedTo = 'assignedTo',
  zendeskTicketsStatus = 'entityCachedData.organization.zendeskTickets.latestComment.status',
  prioritization = 'prioritization',
  isGoodsReceived = 'entityCachedData.features.isGoodsReceived',
  containsSanctionedWords = 'entityCachedData.features.invoiceOrNoteHasSanctionedWord',
  containsScanningIssues = 'entityCachedData.features.invoiceOrNoteHasSanctionedWordError',
  hasFileRequests = 'entityCachedData.features.hasFileRequests',
  concernArea = 'extraFilters',
  concernAreaStatus = 'concernAreas.status'
}

export type CompanyIdField =
  | InboxFilterMapperEnum.organizationId
  | InboxFilterMapperEnum.vendorId;

export const inboxFiltersTypes: IFilterMap = {
  entityCachedData: {
    payment: {
      id: {
        type: Number,
        shouldSupportRange: false
      },
      createdAt: {
        type: Date,
        shouldSupportRange: true
      },
      createdByEmail: {
        type: String,
        shouldSupportRegex: true
      },
      scheduledDate: {
        type: Date,
        shouldSupportRange: true
      },
      amount: {
        type: Number,
        shouldSupportRange: true
      },
      createOrigin: {
        type: String
      },
      direction: {
        type: String
      },
      createdByPartner: {
        companyName: {
          type: String,
          shouldSupportRegex: true
        }
      },
      riskStatus: {
        type: String
      },
      pendingReason: {
        type: String
      }
    },
    deliveryMethod: {
      deliveryType: { type: String },
      createdAt: { type: Date, shouldSupportRange: true }
    },
    organization: {
      id: {
        type: Number
      },
      companyName: {
        type: String,
        shouldSupportRegex: true
      },
      decision: {
        type: String
      },
      kybStatus: {
        type: String
      },
      taxId: {
        type: String,
        shouldSupportRegex: true
      },
      taxIdType: {
        type: String
      },
      kybMoneyIn: {
        type: String
      },
      kybMoneyInSubType: {
        type: String
      },
      partner: {
        partnerName: {
          type: String,
          shouldSupportRegex: true
        }
      },
      zendeskTickets: {
        latestComment: {
          status: {
            type: String
          }
        }
      }
    },
    vendor: {
      id: {
        type: Number
      },
      companyName: {
        type: String,
        shouldSupportRegex: true
      }
    },
    features: {
      hasFileRequests: {
        type: Boolean
      },
      isPaymentExpedited: {
        type: Boolean
      },
      isSuspectedOfProhibitedIndustries: {
        type: Boolean
      },
      isFRR: {
        type: Boolean
      },
      isPaymentOriginSafe: {
        type: Boolean
      },
      fundingSourceType: {
        type: String
      },
      vendorHasHandle: {
        type: Boolean
      },
      isApFinancingPayment: {
        type: Boolean
      },
      isGoodsReceived: {
        type: Boolean
      },
      invoiceOrNoteHasSanctionedWord: {
        type: Boolean
      },
      invoiceOrNoteHasSanctionedWordError: {
        type: Boolean
      }
    },
    riskEngineDecision: {
      reason: {
        type: String
      }
    },
    paymentApprovalAction: {
      reason: {
        type: String
      }
    }
  },
  lastDecisionDetails: {
    lastDecisionDate: {
      type: Date,
      shouldSupportRange: true
    },
    lastDecisionMakerId: {
      type: String
    }
  },
  lastEscalatedDetails: {
    teamIntegrationId: {
      type: String
    },
    userId: {
      type: String
    },
    description: {
      type: String,
      shouldSupportRegex: true
    }
  },
  label: { type: String },
  assignedTo: { type: String },
  prioritization: {
    type: Number,
    shouldSupportRange: true
  },
  extraFilters: {
    type: String
  }
};

export enum SortableProjectField {
  Name = 'name'
}
